

export const INTAKE_QUESTIONS = {

  /* ── Menstrual / Period Problems ───────────────────────────────── */
  menstrual_health: {
    title: "Menstrual Health",
    emoji: "🩸",
    questions: [
      {
        id: "mh_cycle_regularity",
        q: "How regular are your periods?",
        type: "single",
        options: ["Very regular (every 21–35 days)", "Somewhat irregular", "Very irregular", "I've missed periods", "I'm not sure"],
      },
      {
        id: "mh_flow",
        q: "How would you describe your flow?",
        type: "single",
        options: ["Light", "Moderate", "Heavy (soaking a pad/tampon every 1–2 hours)", "Very heavy with clots", "It varies a lot"],
      },
      {
        id: "mh_pain",
        q: "Do you experience pain with your periods?",
        type: "single",
        options: ["No pain", "Mild cramps", "Moderate pain — affects daily activities", "Severe pain — I have to rest or miss work/school", "Pain outside of my period too"],
      },
      {
        id: "mh_symptoms",
        q: "Do you experience any of these alongside your period?",
        type: "multi",
        options: ["Bloating", "Breast tenderness", "Mood swings", "Headaches/migraines", "Nausea", "Fatigue", "None of the above"],
      },
      {
        id: "mh_duration",
        q: "How long does your period typically last?",
        type: "single",
        options: ["Less than 3 days", "3–5 days", "6–7 days", "More than 7 days", "Varies each cycle"],
      },
    ],
  },

  /* ── PCOS / Hormonal Screening ─────────────────────────────────── */
  pcos_screening: {
    title: "Hormonal & PCOS Check",
    emoji: "🔬",
    questions: [
      {
        id: "pcos_periods",
        q: "How often do you get your period?",
        type: "single",
        options: ["Every month (regular)", "Every 35–90 days (infrequent)", "Less than 8 times a year", "Very rarely or not at all", "I'm unsure"],
      },
      {
        id: "pcos_symptoms",
        q: "Are you experiencing any of the following?",
        type: "multi",
        options: ["Excess facial/body hair", "Hair thinning or loss on scalp", "Acne (face, chest, or back)", "Weight gain especially around the waist", "Darkened skin patches (neck, armpits, groin)", "None of the above"],
      },
      {
        id: "pcos_diagnosed",
        q: "Have you ever been diagnosed with PCOS or a thyroid condition?",
        type: "single",
        options: ["Yes — PCOS", "Yes — Thyroid (hypothyroid or hyperthyroid)", "Yes — both", "No", "I've been told I might have it but not officially diagnosed"],
      },
      {
        id: "pcos_tests",
        q: "Have you had any recent hormone or blood tests?",
        type: "single",
        options: ["Yes, within the last 6 months", "Yes, more than 6 months ago", "No", "I don't remember"],
      },
    ],
  },

  /* ── Pelvic Pain ────────────────────────────────────────────────── */
  pelvic_pain: {
    title: "Pelvic Pain",
    emoji: "💜",
    questions: [
      {
        id: "pp_location",
        q: "Where do you feel the pain most?",
        type: "multi",
        options: ["Lower abdomen", "Lower back", "Pelvic floor / between the hip bones", "During sex (deep pain)", "During urination", "During bowel movements"],
      },
      {
        id: "pp_timing",
        q: "When does the pain usually occur?",
        type: "multi",
        options: ["During my period", "Mid-cycle / around ovulation", "All the time (chronic)", "Randomly — no pattern", "During or after sex", "During exercise"],
      },
      {
        id: "pp_severity",
        q: "How severe is the pain on average (1 = barely noticeable, 5 = debilitating)?",
        type: "single",
        options: ["1 — Very mild", "2 — Mild", "3 — Moderate", "4 — Severe", "5 — Debilitating, stops daily life"],
      },
      {
        id: "pp_diagnosis",
        q: "Have you ever been diagnosed with any of the following?",
        type: "multi",
        options: ["Endometriosis", "Fibroids", "Ovarian cysts", "Interstitial cystitis", "IBS or bowel issues", "None / Not diagnosed yet"],
      },
    ],
  },

  /* ── Puberty / Adolescent Education ────────────────────────────── */
  puberty_education: {
    title: "Puberty & Growing Up",
    emoji: "🌸",
    questions: [
      {
        id: "pub_started",
        q: "Have your periods started yet?",
        type: "single",
        options: ["Yes", "Not yet", "I'm not sure"],
      },
      {
        id: "pub_concerns",
        q: "What are you most curious or worried about?",
        type: "multi",
        options: ["Period pain or irregularity", "Mood changes", "Body changes (skin, hair, weight)", "Talking to a parent/guardian about it", "Hygiene and period products", "Something else"],
      },
      {
        id: "pub_support",
        q: "Who do you usually talk to about health questions?",
        type: "single",
        options: ["Parent or guardian", "Friend", "Teacher or counsellor", "Doctor", "No one — I prefer to look it up myself"],
      },
    ],
  },

  /* ── Mental Wellbeing ───────────────────────────────────────────── */
  mental_wellbeing: {
    title: "Mental Wellbeing",
    emoji: "🧠",
    questions: [
      {
        id: "mw_mood",
        q: "How have you been feeling emotionally over the past 2 weeks?",
        type: "single",
        options: ["Mostly okay", "A bit low or anxious", "Frequently sad, hopeless, or worried", "Extreme mood swings — very high then very low", "I've been struggling a lot"],
      },
      {
        id: "mw_sleep",
        q: "How is your sleep?",
        type: "single",
        options: ["Good — 7–9 hours, feel rested", "Trouble falling asleep", "Wake up frequently", "Sleeping too much", "Very poor — affects daily life"],
      },
      {
        id: "mw_symptoms",
        q: "Are you experiencing any of these?",
        type: "multi",
        options: ["Anxiety or constant worry", "Low energy or motivation", "Difficulty concentrating", "Feeling overwhelmed", "Irritability or anger", "Loss of interest in things you enjoy", "None of the above"],
      },
      {
        id: "mw_cycle_link",
        q: "Do your mood changes seem linked to your menstrual cycle?",
        type: "single",
        options: ["Yes — clearly worse before my period (PMS/PMDD)", "Sometimes", "No clear pattern", "I'm not sure", "I don't get periods / post-menopause"],
      },
    ],
  },

  /* ── Preventive / General Check-up ─────────────────────────────── */
  preventive_care: {
    title: "Preventive Health",
    emoji: "✅",
    questions: [
      {
        id: "prev_last_checkup",
        q: "When did you last have a general health check-up?",
        type: "single",
        options: ["Within the last year", "1–2 years ago", "More than 2 years ago", "Never", "I don't remember"],
      },
      {
        id: "prev_screenings",
        q: "Which of these have you had done?",
        type: "multi",
        options: ["Pap smear / cervical screening", "Breast exam or mammogram", "Blood pressure check", "Blood sugar / diabetes screening", "Cholesterol check", "None of the above"],
      },
      {
        id: "prev_family_history",
        q: "Does anyone in your family have a history of?",
        type: "multi",
        options: ["Breast or ovarian cancer", "Cervical cancer", "Heart disease", "Diabetes", "Osteoporosis", "None that I know of"],
      },
      {
        id: "prev_lifestyle",
        q: "How would you rate your current lifestyle?",
        type: "single",
        options: ["Healthy — active and balanced diet", "Mostly okay — could improve", "Quite sedentary or poor diet", "Under a lot of stress", "I've been neglecting my health"],
      },
    ],
  },

  /* ── Doctor Booking ─────────────────────────────────────────────── */
  doctor_booking: {
    title: "Doctor Appointment",
    emoji: "🩺",
    questions: [
      {
        id: "db_reason",
        q: "What's the main reason you want to see a doctor?",
        type: "single",
        options: ["Routine check-up", "A specific symptom or concern", "Follow-up for an existing condition", "Second opinion", "Prescription renewal"],
      },
      {
        id: "db_urgency",
        q: "How soon do you need to be seen?",
        type: "single",
        options: ["Today / urgent", "Within this week", "Within 2 weeks", "Within a month", "No rush — flexible"],
      },
      {
        id: "db_preference",
        q: "Do you have a preference for the type of consultation?",
        type: "single",
        options: ["In-clinic (face-to-face)", "Video consultation", "Chat / messaging only", "No preference"],
      },
    ],
  },

  /* ── Early Pregnancy ────────────────────────────────────────────── */
  early_pregnancy: {
    title: "Early Pregnancy",
    emoji: "🤰",
    questions: [
      {
        id: "ep_test",
        q: "Have you taken a home pregnancy test?",
        type: "single",
        options: ["Yes — positive", "Yes — negative", "Yes — unclear result", "Not yet", "I've had a blood test instead"],
      },
      {
        id: "ep_lmp",
        q: "When was your last menstrual period (approx.)?",
        type: "single",
        options: ["Less than 4 weeks ago", "4–6 weeks ago", "6–8 weeks ago", "8–10 weeks ago", "More than 10 weeks ago", "I'm not sure"],
      },
      {
        id: "ep_symptoms",
        q: "Are you experiencing any of these?",
        type: "multi",
        options: ["Nausea or vomiting", "Breast tenderness", "Fatigue", "Spotting or light bleeding", "Cramping", "None so far"],
      },
      {
        id: "ep_concerns",
        q: "Do you have any immediate concerns?",
        type: "single",
        options: ["Heavy bleeding or severe cramps", "Dizziness or fainting", "No concerns right now", "I just want to confirm the pregnancy"],
      },
    ],
  },

  /* ── Preconception Planning ─────────────────────────────────────── */
  preconception: {
    title: "Preconception Care",
    emoji: "🌱",
    questions: [
      {
        id: "pc_timeline",
        q: "When are you hoping to conceive?",
        type: "single",
        options: ["Within 3 months", "3–6 months", "6–12 months", "In more than a year", "Just exploring options"],
      },
      {
        id: "pc_current_health",
        q: "Do you have any existing health conditions?",
        type: "multi",
        options: ["Thyroid disorder", "PCOS", "Diabetes", "Anaemia", "Hypertension", "None", "Other"],
      },
      {
        id: "pc_supplements",
        q: "Are you taking any preconception supplements?",
        type: "single",
        options: ["Yes — folic acid", "Yes — multiple supplements", "No, not yet", "I'm not sure what I should take"],
      },
      {
        id: "pc_previous",
        q: "Have you been pregnant before?",
        type: "single",
        options: ["No — this will be my first", "Yes — successful pregnancy", "Yes — pregnancy loss (miscarriage/stillbirth)", "Yes — more than one previous pregnancy"],
      },
    ],
  },

  /* ── Fertility ──────────────────────────────────────────────────── */
  fertility: {
    title: "Fertility Assessment",
    emoji: "🌿",
    questions: [
      {
        id: "fer_trying_duration",
        q: "How long have you been trying to conceive?",
        type: "single",
        options: ["Less than 6 months", "6–12 months", "1–2 years", "More than 2 years", "Just starting to think about it"],
      },
      {
        id: "fer_cycle",
        q: "Are your cycles regular?",
        type: "single",
        options: ["Yes — regular 28–35 day cycles", "Somewhat irregular", "Very irregular or absent", "I use tracking apps / OPK kits"],
      },
      {
        id: "fer_tests",
        q: "Have you or your partner had any fertility tests?",
        type: "single",
        options: ["Yes — I have had tests", "Yes — my partner has had tests", "Both of us have been tested", "No tests yet"],
      },
      {
        id: "fer_diagnosis",
        q: "Have you been given any fertility-related diagnosis?",
        type: "multi",
        options: ["PCOS", "Low ovarian reserve (low AMH)", "Blocked tubes", "Uterine fibroids or polyps", "Partner has low sperm count/motility", "Unexplained infertility", "None so far"],
      },
    ],
  },

  /* ── Contraception ──────────────────────────────────────────────── */
  contraception: {
    title: "Contraception",
    emoji: "💊",
    questions: [
      {
        id: "con_current",
        q: "Are you currently using any contraception?",
        type: "single",
        options: ["Yes — combined pill", "Yes — progestogen-only pill (mini-pill)", "Yes — IUD (coil)", "Yes — implant or injection", "Yes — condoms", "No — not using anything", "No — I want to stop my current method"],
      },
      {
        id: "con_goal",
        q: "What are you looking for today?",
        type: "single",
        options: ["Start contraception for the first time", "Switch to a different method", "Understand side effects I'm having", "Emergency contraception advice", "Contraception and period management combined"],
      },
      {
        id: "con_health",
        q: "Do you have any of the following? (affects method suitability)",
        type: "multi",
        options: ["Migraines with aura", "High blood pressure", "History of blood clots (DVT/PE)", "Breast cancer history", "Diabetes", "None of the above"],
      },
    ],
  },

  /* ── Infection / Gynaecological ─────────────────────────────────── */
  infection_gynae: {
    title: "Infection & Gynaecological",
    emoji: "🦠",
    questions: [
      {
        id: "inf_symptoms",
        q: "What symptoms are you experiencing?",
        type: "multi",
        options: ["Unusual discharge (colour, smell, or texture)", "Itching or burning", "Pain during sex", "Pain or burning when urinating", "Fever or chills", "Pelvic pain or pressure"],
      },
      {
        id: "inf_duration",
        q: "How long have you had these symptoms?",
        type: "single",
        options: ["Just started (1–2 days)", "A few days", "About a week", "More than 2 weeks", "On and off for a long time"],
      },
      {
        id: "inf_previous",
        q: "Have you had a similar infection before?",
        type: "single",
        options: ["No — this is new", "Yes — yeast infection (thrush)", "Yes — bacterial vaginosis (BV)", "Yes — UTI", "Yes — STI", "Yes — multiple times"],
      },
      {
        id: "inf_sti_risk",
        q: "Would you like to be screened for STIs as well?",
        type: "single",
        options: ["Yes please", "No, not necessary", "I'm not sure — would like advice"],
      },
    ],
  },

  /* ── Post-Miscarriage Support ───────────────────────────────────── */
  post_miscarriage: {
    title: "Post-Miscarriage Care",
    emoji: "🕊️",
    questions: [
      {
        id: "pm_timing",
        q: "When did the miscarriage occur?",
        type: "single",
        options: ["Within the last week", "1–2 weeks ago", "2–4 weeks ago", "1–3 months ago", "More than 3 months ago"],
      },
      {
        id: "pm_support_type",
        q: "What kind of support are you looking for today?",
        type: "multi",
        options: ["Physical recovery check", "Emotional support / counselling", "Understanding what happened", "Trying to conceive again — guidance", "All of the above"],
      },
      {
        id: "pm_physical",
        q: "Physically, how are you feeling?",
        type: "single",
        options: ["Mostly recovered", "Still experiencing bleeding or pain", "Having hormonal symptoms (mood swings, fatigue)", "Concerned about infection", "Not sure what's normal"],
      },
    ],
  },

  /* ── Lifestyle & Wellness ───────────────────────────────────────── */
  lifestyle: {
    title: "Lifestyle & Wellness",
    emoji: "🌿",
    questions: [
      {
        id: "ls_activity",
        q: "How active are you on a typical week?",
        type: "single",
        options: ["Very active — exercise 5+ times a week", "Moderately active — 2–4 times a week", "Lightly active — once a week or walking", "Mostly sedentary", "I'd like to be more active but struggle"],
      },
      {
        id: "ls_diet",
        q: "How would you describe your diet?",
        type: "single",
        options: ["Balanced — fruits, veg, proteins, whole grains", "Mostly okay — some gaps", "Often skip meals or eat irregularly", "High in processed/fast food", "Following a specific plan (vegetarian, keto, etc.)"],
      },
      {
        id: "ls_stress",
        q: "How are your stress levels?",
        type: "single",
        options: ["Low — I manage well", "Moderate — sometimes overwhelming", "High — affecting sleep, eating, or relationships", "Very high — I need help managing it"],
      },
      {
        id: "ls_goals",
        q: "What are your main wellness goals?",
        type: "multi",
        options: ["Lose or manage weight", "Improve energy levels", "Better sleep", "Reduce stress", "Improve cycle health", "Manage a chronic condition", "General health improvement"],
      },
    ],
  },

  /* ── Menopause Symptoms ─────────────────────────────────────────── */
  menopause_symptoms: {
    title: "Menopause Symptoms",
    emoji: "🌡️",
    questions: [
      {
        id: "ms_symptoms",
        q: "Which symptoms are you experiencing?",
        type: "multi",
        options: ["Hot flushes", "Night sweats", "Sleep disturbances", "Mood changes (anxiety, low mood, irritability)", "Brain fog or memory issues", "Joint pain or stiffness", "Reduced sex drive", "Vaginal dryness", "None of these yet"],
      },
      {
        id: "ms_periods",
        q: "What has happened with your periods?",
        type: "single",
        options: ["Still regular", "Becoming irregular / skipping months", "Very infrequent", "Stopped less than 12 months ago", "Stopped more than 12 months ago (menopause confirmed)"],
      },
      {
        id: "ms_severity",
        q: "How much are symptoms affecting your quality of life?",
        type: "single",
        options: ["Barely noticeable", "Mild — manageable", "Moderate — affecting daily activities", "Severe — significantly impacting work, sleep, or relationships"],
      },
      {
        id: "ms_hrt",
        q: "Have you tried or considered Hormone Replacement Therapy (HRT)?",
        type: "single",
        options: ["Currently on HRT", "Tried HRT but stopped", "Never tried — open to it", "Never tried — not interested", "Want to know more first"],
      },
    ],
  },

  /* ── Heavy Bleeding ─────────────────────────────────────────────── */
  heavy_bleeding: {
    title: "Heavy Bleeding",
    emoji: "🩸",
    questions: [
      {
        id: "hb_description",
        q: "How would you describe the bleeding?",
        type: "single",
        options: ["Heavier than my normal periods", "Soaking through products every 1–2 hours", "Passing large clots", "Bleeding between periods", "Bleeding after menopause", "Bleeding after sex"],
      },
      {
        id: "hb_duration",
        q: "How long has this been happening?",
        type: "single",
        options: ["Just started (today)", "A few days", "This cycle only", "Several months", "Over a year"],
      },
      {
        id: "hb_associated",
        q: "Do you have any of these symptoms alongside the bleeding?",
        type: "multi",
        options: ["Pelvic pain or pressure", "Dizziness or feeling faint", "Extreme fatigue", "Fever", "None of the above"],
      },
      {
        id: "hb_diagnosis",
        q: "Have you been diagnosed with any condition that might cause this?",
        type: "multi",
        options: ["Fibroids", "Endometriosis", "Polyps", "Adenomyosis", "Clotting disorder", "No diagnosis yet"],
      },
    ],
  },

  /* ── Urogenital / Vaginal / Pelvic Floor ────────────────────────── */
  urogenital: {
    title: "Urogenital Health",
    emoji: "💧",
    questions: [
      {
        id: "ug_symptoms",
        q: "What symptoms are you experiencing?",
        type: "multi",
        options: ["Vaginal dryness or itching", "Pain during sex", "Urinary urgency (can't wait to go)", "Urinary leakage (coughing, sneezing, exercise)", "Frequent urination", "Recurrent UTIs", "Pelvic heaviness or prolapse feeling"],
      },
      {
        id: "ug_impact",
        q: "How much is this affecting your daily life?",
        type: "single",
        options: ["Very little", "Some impact — manageable", "Moderate impact — affects activities or intimacy", "Significant impact — interfering with daily life"],
      },
      {
        id: "ug_pelvic_floor",
        q: "Have you done pelvic floor exercises (Kegels) before?",
        type: "single",
        options: ["Yes, regularly", "Tried but not consistent", "No, never", "Not sure how to do them correctly"],
      },
    ],
  },

  /* ── Bone Health / Cardiovascular ──────────────────────────────── */
  bone_health: {
    title: "Bone & Heart Health",
    emoji: "🦴",
    questions: [
      {
        id: "bh_concern",
        q: "What is your main concern?",
        type: "single",
        options: ["Bone density / osteoporosis risk", "Heart health / cardiovascular risk", "Both", "General ageing and wellbeing", "Following up on a previous result"],
      },
      {
        id: "bh_risk_factors",
        q: "Do any of these apply to you?",
        type: "multi",
        options: ["Family history of osteoporosis or fractures", "Family history of heart disease", "Menopause (natural or surgical)", "Steroid medication use (long-term)", "Low calcium or vitamin D intake", "Smoking or heavy alcohol use", "None of the above"],
      },
      {
        id: "bh_tests",
        q: "Have you had any of these tests?",
        type: "multi",
        options: ["DEXA scan (bone density)", "Cholesterol / lipid panel", "Blood pressure check", "ECG", "None yet"],
      },
      {
        id: "bh_exercise",
        q: "Do you do weight-bearing or resistance exercise?",
        type: "single",
        options: ["Yes, regularly", "Occasionally", "Rarely or never", "I'd like to start — need guidance"],
      },
    ],
  },

  /* ── Chronic Disease Management ─────────────────────────────────── */
  chronic_disease: {
    title: "Chronic Condition",
    emoji: "📋",
    questions: [
      {
        id: "cd_conditions",
        q: "Which conditions are you managing?",
        type: "multi",
        options: ["Diabetes (Type 1 or 2)", "Hypertension (high blood pressure)", "Thyroid disorder", "Autoimmune condition (lupus, RA, etc.)", "Chronic pain condition", "Heart disease", "Other"],
      },
      {
        id: "cd_management",
        q: "How well is your condition currently managed?",
        type: "single",
        options: ["Very well — stable and monitored", "Okay — some fluctuations", "Struggling — hard to control", "Newly diagnosed — just figuring it out"],
      },
      {
        id: "cd_medication",
        q: "Are you currently on medication for your condition?",
        type: "single",
        options: ["Yes — and it's working well", "Yes — but I'm having side effects", "Yes — but I keep forgetting to take it", "No medication yet", "I've stopped taking it"],
      },
      {
        id: "cd_goal",
        q: "What would help you most today?",
        type: "single",
        options: ["Review or adjust my medication", "Understand my condition better", "Lifestyle advice to help manage it", "Connect with a specialist", "Regular monitoring plan"],
      },
    ],
  },
};